import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AuiIf, ThreadPrimitive, useAui } from "@assistant-ui/react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearPendingPrompt,
  setActiveContext,
  flushActiveContext,
  setActiveThreadId,
  setActiveWorkflowNode,
  setThreads,
  setThreadsLoading,
  upsertThread,
  renameThread,
  removeThread,
  clearActivity,
  setStatusText,
} from "../../store/slices/copilotSlice";
import { clearAllUploads } from "../../store/slices/recordingUploadSlice";
import { useResumeDeepAnalysis } from "../../hooks/useResumeDeepAnalysis";
import { fetchAlarmById } from "../../lib/alarms";
import { apiClient } from "../../lib/api";
import { selectAllDevices } from "../../store/slices/deviceSlice";
import {
  deleteCopilotThread,
  getCopilotThreadMessages,
  listCopilotThreads,
  updateCopilotThreadTitle,
} from "../../lib/copilotApi";
import {
  chipToAttachment,
  getPageContextSuggestions,
  type ContextChip,
} from "./contextChips";
import {
  LAST_OPENED_THREAD_STORAGE_KEY,
  extractContextSnapshot,
  toRestoredThreadMessages,
  toThreadSummary,
  type SavedConversation,
} from "./threadSerialization";
import { ConversationActions } from "./ConversationActions";
import { ConversationsPanel } from "./ConversationsPanel";
import { CopilotComposer } from "./CopilotComposer";
import { CopilotMessage } from "./CopilotMessage";
import { EmptyState } from "./EmptyState";
import { EditableConversationTitle } from "./EditableConversationTitle";
import { RunningStatusPill } from "./RunningStatusPill";

/**
 * Main copilot panel: header, thread viewport, composer, and the
 * conversation lifecycle (hydration, restore, select, delete, new chat).
 * Must render inside AssistantRuntimeProvider.
 */
export function CopilotPanel({ onClose }: { onClose: () => void }) {
  const aui = useAui();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const pendingPrompt = useAppSelector((s) => s.copilotReducer.pendingPrompt);
  const analyzeContext = useAppSelector((s) => s.copilotReducer.activeContext);
  const threads = useAppSelector((s) => s.copilotReducer.threads);
  const threadsLoading = useAppSelector((s) => s.copilotReducer.threadsLoading);
  const activeThreadId = useAppSelector((s) => s.copilotReducer.activeThreadId);
  const appToken = useAppSelector((s) => s.appAuthReducer.appToken);
  const devices = useAppSelector(selectAllDevices);

  const deviceIdFromPath = pathname.match(/^\/devices\/([^/]+)$/)?.[1];
  const alarmIdFromPath = pathname.match(/^\/alarms\/([^/]+)\/edit$/)?.[1];
  const dashboardIdFromPath = pathname.match(/^\/monitoring\/([^/]+)/)?.[1];
  const panelIdFromPath = pathname.match(
    /^\/monitoring\/[^/]+\/panel\/([^/]+)/,
  )?.[1];
  const deviceFromPath = deviceIdFromPath
    ? devices.find((device) => device.id === deviceIdFromPath)
    : undefined;

  const { data: alarmFromPath } = useQuery({
    queryKey: ["alarm", alarmIdFromPath],
    queryFn: () => fetchAlarmById(alarmIdFromPath!),
    enabled: Boolean(alarmIdFromPath),
    staleTime: 5 * 60 * 1000,
  });
  const { data: dashboardFromPath } = useQuery<{
    name: string;
    panels: Array<{ id: string; title: string }>;
  }>({
    queryKey: ["monitoring-dashboard", dashboardIdFromPath],
    queryFn: () =>
      apiClient
        .get(`/monitoring/${dashboardIdFromPath}`)
        .then((response) => response.data),
    enabled: Boolean(dashboardIdFromPath),
    staleTime: 60 * 1000,
  });

  const [showHistory, setShowHistory] = useState(false);
  const [isRestoringConversation, setIsRestoringConversation] = useState(() => {
    if (typeof window === "undefined") return false;
    return Boolean(
      activeThreadId ||
        window.localStorage.getItem(LAST_OPENED_THREAD_STORAGE_KEY),
    );
  });
  const sentPromptRef = useRef(false);
  const initialThreadRestoreDoneRef = useRef(false);
  const activeThreadIdRef = useRef(activeThreadId);
  activeThreadIdRef.current = activeThreadId;
  const tokenRef = useRef(appToken);
  tokenRef.current = appToken;

  const resumeDeepAnalysis = useResumeDeepAnalysis(tokenRef, activeThreadIdRef);

  // Hydrate the sidebar thread list once per mount.
  useEffect(() => {
    let mounted = true;
    const hydrateThreads = async () => {
      dispatch(setThreadsLoading(true));
      try {
        const items = await listCopilotThreads();
        if (!mounted) return;
        dispatch(setThreads(items.map(toThreadSummary)));
      } catch {
        if (!mounted) return;
        dispatch(setThreads([]));
      } finally {
        if (mounted) dispatch(setThreadsLoading(false));
      }
    };
    hydrateThreads();
    return () => {
      mounted = false;
    };
  }, [dispatch]);

  const pageSuggestions = getPageContextSuggestions(pathname, {
    deviceName: deviceFromPath?.name,
    siteId: deviceFromPath?.site_id,
    siteName: deviceFromPath?.siteName,
    alarmName: alarmFromPath?.name,
    dashboardName: dashboardFromPath?.name,
    panelTitle: panelIdFromPath
      ? dashboardFromPath?.panels.find((panel) => panel.id === panelIdFromPath)
          ?.title
      : undefined,
  });

  // Auto-send a prompt queued by an external trigger (incident "Analyze"
  // action). Deferred a tick so the runtime's context ref is synced first.
  useEffect(() => {
    if (!pendingPrompt) {
      sentPromptRef.current = false;
      return;
    }
    if (sentPromptRef.current) return;
    sentPromptRef.current = true;

    // External analyze actions must always run in a fresh chat, mirroring the
    // "New conversation" behavior while preserving the incident context
    // already written by requestAnalysis.
    dispatch(clearActivity());
    dispatch(setStatusText("IDLE"));
    dispatch(setActiveWorkflowNode(undefined));
    dispatch(clearAllUploads());
    dispatch(setActiveThreadId(undefined));
    aui.thread().reset();
    setShowHistory(false);
    window.localStorage.removeItem(LAST_OPENED_THREAD_STORAGE_KEY);

    const prompt = pendingPrompt;
    const ctxChips: ContextChip[] = [];
    if (analyzeContext.incidentId) {
      ctxChips.push({
        id: "incident",
        label: "Incident",
        displayValue: analyzeContext.incidentReference ?? "Selected incident",
        entityId: analyzeContext.incidentId,
        entityType: "incident",
        icon: "alert",
      });
    }

    const timer = setTimeout(() => {
      try {
        aui.thread().append({
          role: "user",
          content: [{ type: "text", text: prompt }],
          ...(ctxChips.length > 0
            ? { attachments: ctxChips.map(chipToAttachment) }
            : {}),
        });
      } catch {
        // Runtime not ready — the prompt is dropped rather than duplicated.
      }
      dispatch(clearPendingPrompt());
    }, 80);

    return () => clearTimeout(timer);
  }, [pendingPrompt, analyzeContext, aui, dispatch]);

  const handleSelectConversation = useCallback(
    async (conv: SavedConversation) => {
      // Clear the previous thread's live "Thinking" activity so it does not
      // bleed into the newly opened conversation (it is global Redux state,
      // not scoped per thread).
      dispatch(clearActivity());
      dispatch(setStatusText("IDLE"));
      // Drop any in-flight/completed MCAP upload jobs tracked for the previous
      // thread; they are global state and would otherwise surface in the newly
      // opened conversation's approval card. Each thread re-seeds its own jobs
      // from localStorage when its McapApprovalCard mounts.
      dispatch(clearAllUploads());
      try {
        const data = await getCopilotThreadMessages(conv.id);
        // Expose the persisted workflow node BEFORE the transcript renders so
        // the MCAP approval card derives its settled view (uploaded/analysis
        // done) on first paint — never flashing the approval prompt for a
        // workflow that already advanced past the suspension.
        const currentNode =
          typeof data.state?.current_node === "string"
            ? data.state.current_node
            : undefined;
        dispatch(setActiveWorkflowNode(currentNode));
        // Restore the agent's incident/device context so follow-up runs
        // (e.g. action-bar reload) still carry the identifiers the workflow
        // requires — these are lost from Redux on a page refresh.
        dispatch(setActiveContext(extractContextSnapshot(data.state)));
        // Replace (not merge) the runtime's messages. reset() reliably swaps
        // the whole transcript; import() was leaving the previously opened
        // conversation's messages in place, bleeding them across threads.
        const restored = toRestoredThreadMessages(data.messages);
        aui.thread().reset(restored as never);
        // A recorded upload whose analysis never ran (the live run was lost
        // to a refresh): resume it headlessly and reload the thread when the
        // findings have been appended.
        if (currentNode === "ANALYSIS_READY") {
          void resumeDeepAnalysis(conv.id, () =>
            handleSelectConversation(conv),
          );
        }
        dispatch(
          upsertThread({
            id: data.thread.id,
            title: data.thread.title,
            createdAt: data.thread.created_at,
            updatedAt: data.thread.updated_at,
          }),
        );
        dispatch(setActiveThreadId(conv.id));
        window.localStorage.setItem(LAST_OPENED_THREAD_STORAGE_KEY, conv.id);
      } catch {
        aui.thread().reset();
        dispatch(flushActiveContext());
        dispatch(setActiveWorkflowNode(undefined));
      }
      setShowHistory(false);
    },
    [aui, dispatch, resumeDeepAnalysis],
  );

  // On drawer mount/remount, restore the last opened persisted thread so the
  // user lands back in the same conversation rather than a blank new chat.
  useEffect(() => {
    if (initialThreadRestoreDoneRef.current) return;
    if (threadsLoading) return;
    if (threads.length === 0) {
      setIsRestoringConversation(false);
      initialThreadRestoreDoneRef.current = true;
      return;
    }

    const storedId = window.localStorage.getItem(
      LAST_OPENED_THREAD_STORAGE_KEY,
    );
    const targetId = activeThreadId ?? storedId ?? undefined;
    if (!targetId) {
      setIsRestoringConversation(false);
      initialThreadRestoreDoneRef.current = true;
      return;
    }

    const target = threads.find((t) => t.id === targetId);
    if (!target) {
      if (storedId === targetId) {
        window.localStorage.removeItem(LAST_OPENED_THREAD_STORAGE_KEY);
      }
      setIsRestoringConversation(false);
      initialThreadRestoreDoneRef.current = true;
      return;
    }

    setIsRestoringConversation(true);
    void (async () => {
      try {
        await handleSelectConversation({
          id: target.id,
          title: target.title,
          createdAt: new Date(target.createdAt),
        });
      } finally {
        setIsRestoringConversation(false);
      }
    })();

    initialThreadRestoreDoneRef.current = true;
  }, [activeThreadId, handleSelectConversation, threads, threadsLoading]);

  const handleDeleteConversation = useCallback(
    async (id: string) => {
      try {
        await deleteCopilotThread(id);
      } catch {
        return;
      }
      dispatch(removeThread(id));
      if (activeThreadId === id) {
        aui.thread().reset();
        dispatch(clearActivity());
        dispatch(setStatusText("IDLE"));
        dispatch(flushActiveContext());
        dispatch(setActiveWorkflowNode(undefined));
        dispatch(clearAllUploads());
        dispatch(setActiveThreadId(undefined));
        window.localStorage.removeItem(LAST_OPENED_THREAD_STORAGE_KEY);
      }
    },
    [dispatch, activeThreadId, aui],
  );

  const handleRenameConversation = useCallback(
    async (id: string, title: string) => {
      const updatedTitle = await updateCopilotThreadTitle(id, title);
      dispatch(renameThread({ id, title: updatedTitle }));
    },
    [dispatch],
  );

  const handleNewFromPanel = useCallback(() => {
    dispatch(clearActivity());
    dispatch(setStatusText("IDLE"));
    // A fresh conversation must not inherit the previous thread's incident/
    // device context or its tracked MCAP upload jobs.
    dispatch(flushActiveContext());
    dispatch(setActiveWorkflowNode(undefined));
    dispatch(clearAllUploads());
    aui.thread().reset();
    setShowHistory(false);
    dispatch(setActiveThreadId(undefined));
    window.localStorage.removeItem(LAST_OPENED_THREAD_STORAGE_KEY);
  }, [aui, dispatch]);

  const conversations: SavedConversation[] = useMemo(
    () =>
      threads.map((t) => ({
        id: t.id,
        title: t.title,
        createdAt: new Date(t.createdAt),
      })),
    [threads],
  );
  const activeConversationTitle =
    threads.find((t) => t.id === activeThreadId)?.title?.trim() ?? "";

  return (
    <div className="relative flex h-full flex-col bg-neutral-850">
      {showHistory && (
        <ConversationsPanel
          conversations={conversations}
          onSelect={handleSelectConversation}
          onDelete={handleDeleteConversation}
          onRename={handleRenameConversation}
          onClose={() => setShowHistory(false)}
          onNew={handleNewFromPanel}
        />
      )}

      <div className="shrink-0 border-b border-neutral-700/60 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-2 pr-2">
            {activeThreadId && activeConversationTitle && (
              <EditableConversationTitle
                title={activeConversationTitle}
                onSave={(title) =>
                  handleRenameConversation(activeThreadId, title)
                }
              />
            )}
          </div>
          <div className="flex items-center gap-1">
            <ConversationActions
              onToggleHistory={() => setShowHistory((v) => !v)}
              onNew={handleNewFromPanel}
            />
            <button
              onClick={onClose}
              className="rounded-md p-1.5 text-neutral-500 transition-colors hover:bg-neutral-700/60 hover:text-neutral-200"
              aria-label="Close copilot"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      <ThreadPrimitive.Root className="relative flex flex-col flex-1 min-h-0 w-full bg-neutral-850">
        <ThreadPrimitive.Viewport
          autoScroll={true}
          className="flex-1 overflow-y-auto px-4 pt-4 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-700"
        >
          {!isRestoringConversation && (
            <AuiIf condition={(s) => s.thread.isEmpty}>
              <EmptyState />
            </AuiIf>
          )}

          <ThreadPrimitive.Messages>
            {() => <CopilotMessage />}
          </ThreadPrimitive.Messages>
          <AuiIf condition={(s) => s.thread.isRunning}>
            <RunningStatusPill />
          </AuiIf>
          <div className="h-4" />
        </ThreadPrimitive.Viewport>

        {isRestoringConversation && (
          <div className="absolute inset-x-0 top-0 bottom-[88px] z-10 flex items-center justify-center bg-neutral-900/95">
            <div className="flex items-center gap-2 rounded-full border border-neutral-700/80 bg-neutral-900/90 px-3 py-1.5 text-xs text-neutral-300">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-300" />
              Restoring conversation...
            </div>
          </div>
        )}

        <div className="px-4 pb-4 pt-2">
          <CopilotComposer pageSuggestions={pageSuggestions} />
        </div>
      </ThreadPrimitive.Root>
    </div>
  );
}
